<template>
  <div>
    <div class="register-container">
      <form
        class="register"
        ref="registrationForm"
        v-if="inviteKey"
        key="register"
      >
        <p class="page-header">{{ $t('views.register.createAccount') }}</p>

        <s-text-input
          :disabled="!!emailFromUrl"
          v-model="request.Email"
          autocomplete="email"
          id="InputEmail"
          type="email"
          :rounded="true"
          :label="$t('generic.email')"
        ></s-text-input>

        <s-text-input
          v-model="request.Password"
          :type="showPassword ? 'text' : 'password'"
          :icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
          :icon-callback="
            () => {
              showPassword = !showPassword;
            }
          "
          :icon-title="
            showPassword
              ? $t('generic.hidePassword')
              : $t('generic.showPassword')
          "
          autocomplete="off"
          autocorrect="off"
          spellcheck="false"
          :rounded="true"
          id="InputPassword"
          :label="$t('generic.password')"
          @focus="showPasswordIndicator = true"
        ></s-text-input>

        <s-password-indicator
          :show="showPasswordIndicator"
          v-model="request.Password"
          theme="light"
          class="ml20 mb10"
          style="margin-top: -20px"
        ></s-password-indicator>

        <div class="language-picker">
          <span
            v-for="language in $languages"
            class="flag-icon mr10 pointer"
            :class="getFlagIcon(language.LanguageId)"
            :key="language.LanguageId"
            @click="request.Language = language.LanguageId"
          ></span>
        </div>

        <div class="ml20 mt10" v-for="contract in contracts" :key="contract.Id">
          <label :for="contract.Id">
            <input
              type="checkbox"
              :checked="contract._accepted"
              :id="contract.Id"
              class="filled-in"
              @keyup.space.prevent="toggleContract(contract)"
              @click.prevent="toggleContract(contract)"
            />
            <span @click.prevent="toggleContract(contract)"
              >{{ $t('generic.accept') }} {{ contract.Title }}&nbsp;</span
            >
          </label>
          <span
            @click="
              showContract = true;
              activeContract = contract;
            "
            v-if="contract.RequiredRead === false"
            class="pointer showcontract"
            >({{ $t('generic.show') }})</span
          >
        </div>

        <div
          v-if="backendError"
          v-text="`Error: ${backendError}`"
          class="mt10 red-text"
        ></div>

        <flex-grid
          justify="space-around"
          :force-row="true"
          class="button-container mt20"
        >
          <s-button
            :flat="true"
            class="underline"
            :small="true"
            :to="{ name: 'start' }"
            >{{ $t('generic.cancel') }}</s-button
          >
          <s-button
            :small="true"
            @click.prevent="registerUser"
            :loading="loading"
            :disabled="!isRequestValid"
            extra-classes="right"
            color="blue"
            >{{ $t('generic.register') }}</s-button
          >
        </flex-grid>
      </form>

      <form class="register" v-else>
        <p class="page-header mb0">
          {{ $t('views.register.useCode') }}
          <span class="page-sub-header">{{
            $t('views.register.fillCode')
          }}</span>
        </p>
        <s-text-input
          type="text"
          name="key"
          :label="$t('generic.code')"
          v-model="customKey"
          :rounded="true"
          id="InputRegisterCode"
        ></s-text-input>

        <flex-grid
          justify="space-around"
          :force-row="true"
          class="button-container mt20"
        >
          <s-button
            :flat="true"
            class="underline"
            :small="true"
            :to="{ name: 'start' }"
            >{{ $t('generic.cancel') }}</s-button
          >
          <s-button
            @click.prevent="setKey"
            extra-classes="right"
            :small="true"
            :disabled="!customKey"
            color="blue"
            >{{ $t('views.register.useCode') }}</s-button
          >
        </flex-grid>
      </form>

      <!-- <div class="mt10 register">
        <router-link
          class="col s12 right-text"
          :to="{ name: 'start' }"
        >&laquo; {{$t('generic.toStartPage')}}</router-link>
      </div>-->
    </div>

    <modal
      :show="showContract"
      @close="
        showContract = false;
        activeContract = null;
      "
      :scroll-to-accept="activeContract && activeContract.RequiredRead === true"
      :accept-callback="
        () => {
          activeContract._accepted = true;
          showContract = false;
        }
      "
      :hide-accept-button="
        activeContract && activeContract.RequiredRead === false
      "
      :accept-text="$t('generic.accept')"
      :decline-text="$t('generic.close')"
      :disable-close-icon="true"
      width="80vw"
      max-width="800px"
    >
      <contract :contract="activeContract"></contract>

      <template v-slot:extras>
        <div>
          <dl class="list small-font" v-if="activeContract">
            <dt>
              {{ $t('generic.validFrom') }}:
              {{ $filters.DateFormat(activeContract.ValidFromDate) }}
            </dt>
          </dl>
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { LANGUAGE_ACTIONS, LANGUAGE_GETTERS } from '@/store/modules/languages';
import Contract from '@common/Components/Consents/Contract';
import UserService from '@common/Services/User/UserService';
import ContractService from '@common/Services/Contracts/ContractService';
import AuthService from '@common/Services/AuthService';
import storage from '@common/Helpers/storage';
import { getStoredAuthorization } from '@common/Helpers/auth';
import * as languageHelper from '@common/Helpers/language';
import FormSetup from '@common/Mixins/FormSetup';
import FlexGrid from '@common/Components/Grids/FlexGrid.vue';
export default {
  name: 'RegisterUserView',
  mixins: [FormSetup],
  props: {
    inviteKey: {
      type: String,
      required: false
    }
  },
  components: {
    Contract,
    FlexGrid
  },
  data() {
    return {
      loading: false,
      showContract: false,
      showPassword: false,
      request: {
        Email: null,
        Password: null,
        Language: 'sv'
      },
      languages: [],
      customKey: null,
      emailFromUrl: null,
      buttonDisabled: true,
      contracts: null,
      userid: null,
      activeContract: null,
      backendError: null,
      showPasswordIndicator: false
    };
  },

  mounted() {
    if (this.inviteKey) {
      if (
        this.$route.query.email !== null &&
        this.$route.query.email !== undefined
      ) {
        try {
          this.emailFromUrl = atob(this.$route.query.email);
          this.request.Email = this.emailFromUrl;
          this.$_FormSetup_setupForm(200);
        } catch (error) {
          this.$router.push('/');
        }

        this.checkEmail().catch(error => {
          this.$router.push('/');
        });
      }
      this.checkInviteKey();
      this.getContracts();
    }

    setTimeout(() => {
      this.$_FormSetup_setupForm();
    }, 100);

    this.$getLanguages().then(() => {
      this.$_FormSetup_setupForm();
    });
  },

  watch: {
    inviteKey(value) {
      if (!value) {
        return;
      }
      this.checkInviteKey();
      this.getContracts();
    },
    request: {
      handler(newValue, oldValue) {
        this.backendError = null;
      },
      deep: true
    },
    'request.Language'(value) {
      this.getContracts();
      languageHelper.loadLanguageAsync(value);
    }
  },

  methods: {
    ...mapActions('languages', {
      $getLanguages: LANGUAGE_ACTIONS.GET_LANGUAGES
    }),
    getContracts() {
      ContractService.getContractsFromInvite(
        this.inviteKey,
        this.request.Language
      ).then(contracts => {
        this.contracts = contracts.filter(
          contract => contract.Required === true
        );
      });
    },
    getFlagIcon(languageId) {
      const flags = { sv: 'se', en: 'gb' };

      let flagIconCss = 'flag-icon-' + flags[languageId];

      if (languageId === this.request.Language) {
        flagIconCss += ' active';
      }

      return flagIconCss;
    },
    checkEmail() {
      return UserService.checkEmail(this.inviteKey, this.request.Email);
    },
    checkInviteKey() {
      UserService.checkRegisterKey(this.inviteKey)
        .then(() => UserService.getInvite(this.inviteKey))
        .catch(() => {
          if (getStoredAuthorization()) {
            this.$router.push({ name: 'start' });
          } else {
            this.$router.push({ name: 'loginForm' });
          }
        });
    },
    toggleContract(contract) {
      if (contract.RequiredRead === false) {
        contract._accepted = !contract._accepted;
        return;
      }
      if (contract._accepted === true) {
        contract._accepted = false;
      } else {
        console.log('display');
        this.showContract = true;
        this.activeContract = contract;
      }
    },
    setKey() {
      this.$router.push({
        name: 'register',
        params: { inviteKey: this.customKey }
      });
    },
    registerUser() {
      this.loading = true;
      this.createUserAndAcceptContracts().then(() => this.login());
    },
    login() {
      this.$router.push({ name: 'start' });
    },
    createUserAndAcceptContracts() {
      return UserService.registerUser(
        { ...this.request, ConsentedContracts: this.contractGuids },
        this.inviteKey
      )
        .then(userInfo =>
          AuthService.login(
            userInfo.Username,
            this.request.Password,
            userInfo.Groups[0].Roles[0]
          )
        )
        .then(details => storage.set('auth', details))
        .catch(e => {
          this.loading = false;
          this.backendError = e.Message;
          throw e;
        });
    }
  },

  computed: {
    ...mapGetters('languages', {
      $languages: LANGUAGE_GETTERS.LANGUAGES
    }),
    isRequestValid() {
      if (!this.contracts) {
        return;
      }
      const allContractsAreAccepted = this.contracts.every(
        contract => contract._accepted === true
      );
      return allContractsAreAccepted === true;
    },
    contractGuids() {
      return this.contracts.map(c => c.Id);
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@sass/_variables.scss';
.fullscreen-center {
  flex-direction: column;
}
input {
  @media only screen and (max-width: $breakpoint-phone) {
    min-width: 100% !important;
  }
}

.flag-icon {
  opacity: 0.5;
}

.active {
  opacity: 1;
}

.register-container {
  display: flex;

  justify-content: center;
  flex-direction: column;

  svg {
    max-width: 100%;
    fill: $symptoms-blue;
  }

  .register {
    color: black !important;

    .language-picker {
      position: absolute;
      top: 0;
      right: 0;
      display: none;
    }

    label {
      color: inherit;
    }
  }
}
.row .col.s12 {
  @media only screen and (max-width: $breakpoint-phone) {
    width: 99%;
  }
}

.showcontract:hover {
  text-decoration: underline;
}
</style>
